import type ReactAce from 'react-ace';
import { Ace } from 'ace-builds';
import { ReactNode, useEffect, useRef, useState } from 'react';
import * as R from 'remeda';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  IconAlertOctagon,
  IconAlignLeft,
  IconArrowsMaximize,
  IconArrowsMinimize,
  IconDeviceFloppy,
  IconRefresh,
} from '@rossum/ui/icons/tabler';
import {
  Box,
  Dialog,
  IconButton,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
} from '@rossum/ui/material';
import { borderRadius } from '@rossum/ui/theme';
import JsonEditor from '../../../../../components/UI/Editor/components/JsonEditor';
import { JSON_FIELD_ANNOTATION_SOURCE } from '../../../lib/schemaValidation';
import { useJSONfield } from '../../../lib/useJSONfield';

type JsonInputProps = {
  name: string;
  height: number;
  jsonFieldProps: ReturnType<typeof useJSONfield>;
  title?: ReactNode;
  expandable?: boolean;
};

const JsonInputBase = ({
  name,
  height,
  jsonFieldProps: {
    onChange,
    value,
    error,
    schemaErrorAnnotations,
    onPrettify,
    canPrettify,
    onSave,
    canSave,
    onReset,
    canReset,
    onValidate,
  },
  onOpenInFullToggle,
  isOpenInFull,
  title,
  expandable,
}: JsonInputProps & {
  onOpenInFullToggle: () => void;
  isOpenInFull: boolean;
}) => {
  const intl = useIntl();
  const hasError = !!error;

  // This is a workaround for setting the annotations to ACE editor.
  // Ideally we would only need to pass annotations as prop to component
  // but there is a bug that overwrites annotations from worker
  // https://github.com/securingsincity/react-ace/issues/483
  const editor = useRef<ReactAce | null>(null);
  useEffect(() => {
    const editorSession = editor.current?.editor.getSession();
    const onChangeHandler = () => {
      const annotations: Array<Ace.Annotation & { source?: string }> =
        editorSession?.getAnnotations() ?? [];
      const jsonEditorAnnotations = annotations.filter(
        ({ source }) => source === JSON_FIELD_ANNOTATION_SOURCE
      );
      // we will never have messages from worker and schema validation at the same time
      if (
        editorSession &&
        !R.isDeepEqual(jsonEditorAnnotations, schemaErrorAnnotations)
      ) {
        editorSession.setAnnotations(schemaErrorAnnotations);
      }
    };

    if (editorSession) {
      editorSession.on('changeAnnotation', onChangeHandler);
    }

    return () => {
      if (editorSession) {
        editorSession.removeEventListener('changeAnnotation', onChangeHandler);
      }
    };
  }, [editor, schemaErrorAnnotations]);

  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent={isOpenInFull ? 'space-between' : 'flex-end'}
        sx={{
          px: 1,
          py: 1,
          backgroundColor: theme =>
            theme.palette.mode === 'light'
              ? 'action.selected'
              : 'background.default',
          borderBottom: theme => `1px solid ${theme.palette.divider}`,
          borderTopLeftRadius: isOpenInFull
            ? 0
            : theme => borderRadius.abs(theme.shape.borderRadius, 'm'),
          borderTopRightRadius: isOpenInFull
            ? 0
            : theme => borderRadius.abs(theme.shape.borderRadius, 'm'),
          borderBottomRightRadius: 0,
          borderBottomLeftRadius: 0,
        }}
      >
        {isOpenInFull && (
          <>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Tooltip
                placement="top"
                title={intl.formatMessage({
                  id: 'components.editor.tooltips.prettify',
                })}
              >
                <span>
                  <IconButton
                    disabled={!canPrettify}
                    color="inherit"
                    size="small"
                    onClick={onPrettify}
                  >
                    <SvgIcon fontSize="large">
                      <IconAlignLeft />
                    </SvgIcon>
                  </IconButton>
                </span>
              </Tooltip>
              {onSave && (
                <Tooltip
                  placement="top"
                  title={intl.formatMessage({
                    id: 'components.editor.tooltips.save',
                  })}
                >
                  <IconButton
                    disabled={!canSave}
                    color="inherit"
                    size="small"
                    onClick={onSave}
                  >
                    <SvgIcon fontSize="large">
                      <IconDeviceFloppy />
                    </SvgIcon>
                  </IconButton>
                </Tooltip>
              )}
              {onReset && (
                <Tooltip
                  placement="top"
                  title={intl.formatMessage({
                    id: 'components.editor.tooltips.reset',
                  })}
                >
                  <IconButton
                    disabled={!canReset}
                    color="inherit"
                    size="small"
                    onClick={onReset}
                  >
                    <SvgIcon fontSize="large">
                      <IconRefresh />
                    </SvgIcon>
                  </IconButton>
                </Tooltip>
              )}
            </Stack>

            <Typography variant="body2">{title}</Typography>
          </>
        )}
        <Tooltip
          placement="top"
          title={intl.formatMessage({
            id: isOpenInFull
              ? 'components.editor.inputBox.minimize'
              : 'components.editor.inputBox.extend',
          })}
        >
          <Stack
            sx={{
              visibility: expandable ? 'visible' : 'hidden',
              pointerEvents: expandable ? 'auto' : 'none',
            }}
          >
            <IconButton
              color="inherit"
              size="small"
              onClick={onOpenInFullToggle}
              disabled={!expandable}
            >
              <SvgIcon fontSize={isOpenInFull ? 'medium' : 'small'}>
                {isOpenInFull ? <IconArrowsMinimize /> : <IconArrowsMaximize />}
              </SvgIcon>
            </IconButton>
          </Stack>
        </Tooltip>
      </Stack>

      <JsonEditor
        name={name}
        onChange={onChange}
        value={value}
        forwardRef={editor}
        // debounce is not needed anymore - but when omitted, the editor in JSON mode
        // is quiet slow - 0 seems to be solving the issue
        debounceChangePeriod={0}
        height={isOpenInFull ? undefined : height}
        onValidate={onValidate}
      />
      {hasError && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          sx={{
            backgroundColor: 'error.main',
            color: 'white',
            borderRadius: borderRadius('m'),
            padding: '4px 10px',
            position: 'absolute',
            bottom: '10px',
            left: '50%',
            transform: 'translateX(-50%)',
          }}
        >
          <SvgIcon fontSize="medium">
            <IconAlertOctagon />
          </SvgIcon>
          <Typography>
            <FormattedMessage id="components.editor.inputBox.errorMessage" />
          </Typography>
        </Stack>
      )}
    </>
  );
};

export const JsonInput = (props: JsonInputProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  return (
    <Box sx={{ position: 'relative' }}>
      <JsonInputBase
        {...props}
        onOpenInFullToggle={() => setIsDialogOpen(true)}
        isOpenInFull={false}
      />
      <Dialog
        open={isDialogOpen}
        fullScreen
        maxWidth="lg"
        onClose={() => setIsDialogOpen(false)}
        PaperProps={{
          elevation: 2,
          sx: {
            height: '100%',
          },
        }}
      >
        <JsonInputBase
          {...props}
          onOpenInFullToggle={() => setIsDialogOpen(false)}
          isOpenInFull
        />
      </Dialog>
    </Box>
  );
};
